/**
 * anvone – Welcome Gift Script
 *
 * Logic:
 * - Customer selects monthly subscription on product page → Bottle, Welcome
 *   Flyer & Welcome Box are added to the cart as complimentary gifts
 * - Customer switches to one-time purchase → the three gifts are removed
 * - Customer removes Vitality subscription from the cart (on any page,
 *   including /cart) → gifts are automatically removed
 *
 * PIXEL FIX (2026-03-13):
 * Gift additions/removals now use window.__anvone_native_fetch (saved in
 * the head interceptor BEFORE Shopify's pixel system wraps fetch). This
 * means Shopify's Customer Events never sees /cart/add.js or /cart/update.js
 * calls for gifts, so no phantom AddToCart events fire in Meta, Google, etc.
 *
 * IMPORTANT: This script must be loaded in layout/theme.liquid (NOT in
 * sections/main-product.liquid) so it runs on ALL pages including /cart.
 *
 * Installation:
 * 1. Upload to Theme > Assets > anvone-welcome-gift.js
 * 2. Ensure the head interceptor in theme.liquid exposes
 *    window.__anvone_native_fetch (see comment in theme.liquid <head>)
 * 3. Add before </body> in layout/theme.liquid:
 *    <script src="{{ 'anvone-welcome-gift.js' | asset_url }}" defer></script>
 * 4. REMOVE the old <script> tag from sections/main-product.liquid if present
 */

(function () {

  // ─── Configuration ────────────────────────────────────────────────────────

  const CONFIG = {
    // Only active on the Vitality product page (product ID)
    targetProductId: 15429629444480,

    // Selling Plan ID of the monthly subscription (from Loop Widget)
    subscriptionSellingPlanId: 712951038336,

    // Variant IDs of the three welcome gifts
    giftVariantIds: [
      57380481925504, // anvone Bottle
      57380482810240, // anvone Welcome Flyer
      57380483432832, // anvone Welcome Box
    ],

    // Variant ID of the Vitality product (for cart monitoring)
    vitalityVariantId: 56863088017792,

    // Cart line item property used to tag our gifts so we can identify them
    giftCartProperty: '_anvone_welcome_gift',
  };

  // ─── Helper: Pixel-safe fetch ─────────────────────────────────────────────
  //
  // Uses the native browser fetch (saved by the head interceptor BEFORE
  // Shopify's pixel system wrapped it). Calls made through this function
  // are invisible to all App Pixels (Meta, Google, Klaviyo, TrueProfit).
  // Falls back to regular fetch if the native reference isn't available.

  function silentFetch(url, options) {
    var fn = window.__anvone_native_fetch || window.fetch;
    return fn(url, options);
  }

  // ─── Helper Functions ─────────────────────────────────────────────────────

  /**
   * Shopify Cart API: fetch the current cart
   * (Reading the cart is safe – no pixel fires on GET /cart.js)
   */
  async function fetchCart() {
    const res = await fetch('/cart.js');
    return res.json();
  }

  /**
   * Shopify Cart API: add the three welcome gifts to the cart
   * Uses silentFetch so App Pixels don't see the AddToCart
   */
  async function addGiftsToCart() {
    const items = CONFIG.giftVariantIds.map((variantId) => ({
      id: variantId,
      quantity: 1,
      properties: {
        [CONFIG.giftCartProperty]: 'true',
      },
    }));

    const res = await silentFetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items }),
    });

    if (!res.ok) {
      console.error('[anvone] Error adding welcome gifts', await res.text());
      return false;
    }

    // Refresh cart drawer / mini cart (Dawn standard event)
    document.dispatchEvent(new CustomEvent('cart:refresh'));
    refreshCartDrawer();

    return true;
  }

  /**
   * Shopify Cart API: remove all welcome gifts from the cart
   * Uses silentFetch so App Pixels don't see the removal
   */
  async function removeGiftsFromCart() {
    const cart = await fetchCart();

    const updates = {};
    cart.items.forEach((item) => {
      if (item.properties && item.properties[CONFIG.giftCartProperty] === 'true') {
        updates[item.key] = 0;
      }
    });

    if (Object.keys(updates).length === 0) return;

    const res = await silentFetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates }),
    });

    if (!res.ok) {
      console.error('[anvone] Error removing welcome gifts', await res.text());
      return;
    }

    document.dispatchEvent(new CustomEvent('cart:refresh'));
    refreshCartDrawer();
  }

  /**
   * Dawn Section Rendering API: reload the cart drawer and icon bubble
   */
  function refreshCartDrawer() {
    fetch('/?sections=cart-drawer,cart-icon-bubble')
      .then((res) => res.json())
      .then((data) => {
        const drawerEl = document.getElementById('CartDrawer');
        if (drawerEl && data['cart-drawer']) {
          drawerEl.innerHTML = new DOMParser()
            .parseFromString(data['cart-drawer'], 'text/html')
            .getElementById('CartDrawer').innerHTML;
        }

        const bubbleEl = document.getElementById('cart-icon-bubble');
        if (bubbleEl && data['cart-icon-bubble']) {
          bubbleEl.innerHTML = new DOMParser()
            .parseFromString(data['cart-icon-bubble'], 'text/html')
            .getElementById('cart-icon-bubble').innerHTML;
        }
      })
      .catch(() => {});
  }

  /**
   * Check if the subscription option is currently selected on the product page
   */
  function isSubscriptionSelected() {
    const subscriptionOption = document.querySelector(
      '[data-loop-widget-selling-plan-group][aria-checked="true"]'
    );
    return !!subscriptionOption;
  }

  /**
   * Check if welcome gifts are already in the cart
   */
  async function giftsAlreadyInCart() {
    const cart = await fetchCart();
    return cart.items.some(
      (item) =>
        item.properties && item.properties[CONFIG.giftCartProperty] === 'true'
    );
  }

  // ─── Product Page: Loop Widget Observer ───────────────────────────────────

  async function handlePurchaseOptionChange() {
    if (isSubscriptionSelected()) {
      const alreadyAdded = await giftsAlreadyInCart();
      if (!alreadyAdded) {
        await addGiftsToCart();
        console.log('[anvone] Welcome gifts added to cart');
      }
    } else {
      await removeGiftsFromCart();
      console.log('[anvone] Welcome gifts removed (switched to one-time)');
    }
  }

  function initObserver() {
    const widgetContainer = document.getElementById(
      `loop-widget-container-id-${CONFIG.targetProductId}`
    );

    if (!widgetContainer) {
      if (!initObserver._retryCount) initObserver._retryCount = 0;
      initObserver._retryCount++;
      if (initObserver._retryCount < 30) {
        setTimeout(initObserver, 100);
      }
      return;
    }

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'aria-checked'
        ) {
          handlePurchaseOptionChange();
          break;
        }
      }
    });

    observer.observe(widgetContainer, {
      attributes: true,
      subtree: true,
      attributeFilter: ['aria-checked'],
    });

    console.log('[anvone] Loop Widget observer initialized');
    handlePurchaseOptionChange();
  }

  // ─── Global: Cart Monitoring ──────────────────────────────────────────────

  function initCartObserver() {
    let isProcessing = false;
    let lastCartToken = null;

    async function checkVitalityAndCleanup() {
      if (isProcessing) return;
      isProcessing = true;

      try {
        await new Promise((resolve) => setTimeout(resolve, 250));

        const cart = await fetchCart();
        lastCartToken = cart.token;

        const vitalityAboInCart = cart.items.some(
          (item) =>
            item.variant_id === CONFIG.vitalityVariantId &&
            item.selling_plan_allocation !== null &&
            item.selling_plan_allocation !== undefined
        );

        const giftsInCart = cart.items.some(
          (item) =>
            item.properties &&
            item.properties[CONFIG.giftCartProperty] === 'true'
        );

        if (!giftsInCart || vitalityAboInCart) return;

        const isProductPage = !!document.getElementById(
          'loop-widget-container-id-' + CONFIG.targetProductId
        );
        if (isProductPage && isSubscriptionSelected()) {
          return;
        }

        console.log('[anvone] Gifts without Vitality subscription detected – removing');
        await removeGiftsFromCart();

      } catch (e) {
        console.error('[anvone] Error during cart cleanup check', e);
      } finally {
        isProcessing = false;
      }
    }

    document.addEventListener('anvone:cart-changed', checkVitalityAndCleanup);

    async function pollCart() {
      if (isProcessing) return;
      try {
        const cart = await fetchCart();
        if (cart.token !== lastCartToken) {
          lastCartToken = cart.token;
          checkVitalityAndCleanup();
        }
      } catch (e) {
        // Silently ignore
      }
    }
    setInterval(pollCart, 2000);

    checkVitalityAndCleanup();

    console.log('[anvone] Cart observer initialized');
  }

  // ─── Start ────────────────────────────────────────────────────────────────

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initObserver();
      initCartObserver();
    });
  } else {
    initObserver();
    initCartObserver();
  }

})();